import React, { useEffect, useState, useContext } from "react";
import {
  NewBox as Box,
  NewSelect as Select,
  NewButton as Button,
  NewLink as Link,
  NewIcon as Icon,
  useViewport,
  NewPagination as Pagination,
} from "@bca/ui-library";
import styled from "styled-components";
import jwt_decode from "jwt-decode";
import Head from "next/head";
import { useRouter } from "next/router";
import nookies from "nookies";

import Layout from "../src/Layout";
import redirectToIdp from "../src/sso/idp";
import getEnvironment from "../src/getEnvironment";
import getToken from "../src/sso/getToken";
import { DecodedToken } from "../src/authentication.type";
import { devices } from "../src/constants";
import SavedSearchesDesktopView from "../src/SavedSearches/SavedSearchesDesktopView/SavedSearchesDesktopView";
import SavedSearchesMobileView from "../src/SavedSearches/SavedSearchesMobileView/SavedSearchesMobileView";
import { useGetSavedSearches } from "../src/hooks/useGetSavedSearches";
import pageViewDataPush from "../src/SavedSearches/AdobeDataPushes/pageViewDataPush";
import { ErrorContext } from "../src/context/ErrorContext";
import GeneralErrorMessage from "../src/components/ErrorHandling/GeneralErrorMessage";
import NoResultsGeneral from "../src/components/NoResults/NoResultsGeneral";
import CustomizableErrorBanner from "../src/components/ErrorHandling/CustomizableErrorBanner/CustomizableErrorBanner";
import NonMobileLoadingTable from "../src/loading-skeleton/LoadingTable/NonMobileLoadingTable";
import MobileLoadingTable from "../src/loading-skeleton/LoadingTable/MobileLoadingTable";
import { FeatureFlagManager } from "bca-feature-flag-provider";
import { StoreType } from "../src/StoreType";
import { useDispatch, useSelector } from "react-redux";
import {
  GET_STATE,
  SET_IS_TO_USE_AUTH_ZERO,
} from "../src/redux/slices/appSlice";
import { useAuth } from "@bca-auth0/bca-auth0-provider";
import ErrorPage from "../src/common/ErrorPage/ErrorPage";
import useUserDataAnalytics from "../src/hooks/useUserDataAnalytics";

const PageContentContainer = styled(Box)`
  @media ${devices.mobile} {
    padding: 24px 20px;
  }

  @media ${devices.tablet} {
    padding: 24px 40px;
  }

  @media ${devices.desktop} {
    padding: 24px 60px;
  }
`;

const SortByContainer = styled(Box)`
  @media ${devices.mobile} {
    width: 160px;
  }

  @media ${devices.mobileL} {
    width: 180px;
  }
`;

const SavedSearches: React.FC<any> = ({
  user: serverTokenUser,
  idToken: serverIdToken,
  aaId,
  cas,
  ssoId,
  userType,
  isToUseAuthZeroProp,
  authLogoutUrl,
}) => {
  const router = useRouter();
  const errorContext = useContext(ErrorContext);

  const [sortFilter, setSortFilter] = useState("modifiedDate");

  const viewport = useViewport();
  const dispatch = useDispatch();

  const {
    app: { isToUseAuthZero },
  }: StoreType = useSelector(GET_STATE);

  const {
    isAuthenticated,
    authUser,
    isLoading: isAuthLoading,
    error: authError,
    idToken: authIdToken,
  } = useAuth(!isToUseAuthZero);

  const { userAnalyticsData } = useUserDataAnalytics(
    authUser,
    {
      userType,
      aaId,
      ssoId,
      cas,
    },
    isToUseAuthZero
  );

  const user = isToUseAuthZero && isAuthenticated ? authUser : serverTokenUser;
  const idToken =
    isToUseAuthZero && isAuthenticated ? authIdToken : serverIdToken;

  const { query } = router;
  const { savedSearchResults, isLoading } = useGetSavedSearches(
    query,
    isToUseAuthZeroProp
  );

  const retrieveAmountOptions = [10, 25, 50, 100];

  useEffect(() => {
    if (!userAnalyticsData) return;
    pageViewDataPush(
      userAnalyticsData.aaId,
      userAnalyticsData.cas,
      userAnalyticsData.ssoId,
      userAnalyticsData.userType
    );
  }, [userAnalyticsData]);

  useEffect(() => {
    const newQuery = {
      ...router.query,
      sortDescending: sortFilter,
    };

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
  }, [sortFilter]);

  const onSortChange = (event) => {
    setSortFilter(event.target.value);
  };

  useEffect(() => {
    const amount = Number(router.query.amountToRetrieve);

    if (!retrieveAmountOptions.includes(amount)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            amountToRetrieve: 10,
          },
        },
        undefined,
        { shallow: true }
      );
    }
  }, []);

  const onPageChange = (newValue: number) => {
    const newQuery = {
      ...router.query,
      page: newValue,
    };

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
  };

  const onItemsPerPageChange = (newValue: number) => {
    const newQuery = {
      ...router.query,
      amountToRetrieve: newValue,
      page: 1,
    };

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
  };

  const openStockAlertsPage = () => {
    window.location.href = "/search/StockAlerts";
  };

  const shouldShowErrorState = () => {
    return (
      errorContext?.state.somethingWentWrong &&
      errorContext?.state.type === "saved-searches"
    );
  };

  const shouldShowDeleteErrorBanner = () => {
    return (
      errorContext?.state.somethingWentWrong &&
      errorContext?.state.type === "delete-saved-searches"
    );
  };

  const shouldShowEmptystate = () => {
    return savedSearchResults?.count === 0;
  };

  const shouldShowLoadingState = () => {
    return (
      isLoading ||
      (isToUseAuthZero && isAuthLoading && !isAuthenticated && !authError)
    );
  };

  const shouldShowListContent = () => {
    return (
      !shouldShowEmptystate() &&
      !shouldShowErrorState() &&
      !shouldShowLoadingState()
    );
  };

  const loadingState = () => {
    if (viewport.greaterThan.md) {
      return <NonMobileLoadingTable />;
    } else {
      return <MobileLoadingTable />;
    }
  };

  useEffect(() => {
    dispatch(SET_IS_TO_USE_AUTH_ZERO(isToUseAuthZeroProp));
  }, [isToUseAuthZeroProp, dispatch]);

  if (isToUseAuthZero && authError) {
    return (
      <ErrorPage
        user={user}
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
      />
    );
  }

  return (
    <Layout
      user={user}
      idToken={idToken}
      isAuthenticated={isAuthenticated}
      isToUseAuthZero={isToUseAuthZero}
      logoutUrl={authLogoutUrl}
    >
      <Head>
        <title>Saved Searches</title>
      </Head>
      <PageContentContainer>
        <Link
          href="/search"
          inline={false}
          icon={
            <Icon
              name="chevron-left"
              color="accent"
              size="xs"
              marginRight="xs"
            />
          }
          size="sm"
        >
          Vehicle Search
        </Link>
        {shouldShowLoadingState() && loadingState()}
        {shouldShowErrorState() && <GeneralErrorMessage />}
        {shouldShowEmptystate() && <NoResultsGeneral />}
        {shouldShowListContent() && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="baseline"
              marginTop="sm"
            >
              <SortByContainer aria-label="Sort by container" width={200}>
                <Select
                  value={sortFilter}
                  onChange={onSortChange}
                  label="Sort by"
                  height="lg"
                  native
                >
                  <Select.Option value="modifiedDate">
                    Last modified
                  </Select.Option>
                  <Select.Option value="name">Name</Select.Option>
                  <Select.Option value="createdOn">Date created</Select.Option>
                  <Select.Option value="deactivationDate">
                    Alerts stop date
                  </Select.Option>
                </Select>
              </SortByContainer>
              <Button
                text="Create New"
                icon={viewport.greaterThan.sm ? "plus" : null}
                iconPosition="left"
                onClick={openStockAlertsPage}
              />
            </Box>

            {shouldShowDeleteErrorBanner() && (
              <CustomizableErrorBanner
                title="Failed to delete the selected saved search"
                text="We may encounter some technical issue, please try again later"
              />
            )}

            {viewport.greaterThan.md ? (
              <SavedSearchesDesktopView
                savedData={savedSearchResults}
                isToUseAuthZero={isToUseAuthZeroProp}
              />
            ) : (
              <SavedSearchesMobileView
                savedData={savedSearchResults}
                isToUseAuthZero={isToUseAuthZeroProp}
              />
            )}
            <Box paddingTop="lg">
              <Pagination
                numberOfItems={savedSearchResults?.count}
                onPageChange={onPageChange}
                onItemsPerPageChange={onItemsPerPageChange}
                showResultsDisplayedCount={false}
                defaultItemsPerPage={Number(
                  router.query.amountToRetrieve ?? 10
                )}
                defaultPage={Number(router.query.page ?? 1)}
                aria-label="Saved searches pagination"
              />
            </Box>
          </>
        )}
      </PageContentContainer>
    </Layout>
  );
};

export const getServerSideProps = async (context: any) => {
  const featureFlagManager = new FeatureFlagManager();
  await featureFlagManager.initializeFeatureManager(
    process.env.APP_CONFIGURATION_URL
  );
  const useAuthZeroResult =
    await featureFlagManager.getFeatureFlagStatus("authzero");

  const isToUseAuthZero =
    !useAuthZeroResult.isError && useAuthZeroResult.isEnabled;

  if (isToUseAuthZero) {
    let props: any = {};
    props.authProviderDomain = process.env.AUTH0_DOMAIN;
    props.authProviderClientId = process.env.AUTH0_CLIENT_ID;
    props.authProviderAudience = process.env.AUTH0_AUDIENCE;
    props.authProviderScope = process.env.AUTH0_SCOPE;
    props.redirectUri = process.env.AUTH_PROVIDER_SEARCH_REDIRECT_URI;
    props.isToUseAuthZeroProp = isToUseAuthZero;
    props.authLogoutUrl = process.env.AUTH0_LOGOUT_URL;
    props.appConfigurationUrl = process.env.APP_CONFIGURATION_URL;

    return {
      props,
    };
  } else {
    redirectToIdp(
      context,
      getEnvironment(),
      process.env.VIRTUAL_DOMAIN as string,
      process.env.URL_SUFFIX as string
    );

    let props: any = {};

    const authToken = getToken(nookies.get(context));
    const idToken = nookies.get(context).idtoken || "";

    if (!authToken) {
      return { props: {} };
    }

    let firstName: string = "";
    let lastName: string = "";
    let email = "";
    let userType: string = "";
    let aaId: string = "";
    let ssoId: string = "";
    let cas: string = "";

    if (authToken) {
      const decodedToken = jwt_decode(authToken) as DecodedToken;
      email = decodedToken.email;
      firstName = decodedToken.user_name.split(" ")[0];
      lastName = decodedToken.user_name.split(" ")[1];
      userType = decodedToken?.user_type;
      aaId = decodedToken?.peep_analyticsid;
      ssoId = decodedToken?.sub;
      cas = decodedToken?.sourceaccount_id || null;
    }

    const user = {
      email,
      firstName,
      lastName,
    };

    props = {
      user,
      userType,
      aaId,
      ssoId,
      cas,
      idToken,
      isToUseAuthZeroProp: isToUseAuthZero,
      appConfigurationUrl: process.env.APP_CONFIGURATION_URL,
    };

    return {
      props,
    };
  }
};

export default SavedSearches;
